import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR } from "../constants";

interface LeadsByDayChartProps {
  data: LeadsManagementData;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// Builds every calendar day between the two (inclusive) yyyy-mm-dd bounds.
// Parsed as local midnight (not UTC) so the generated iso keys line up with
// toIsoDate's local getFullYear/getMonth/getDate below.
function buildDayRange(startIso: string, endIso: string): Date[] {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days: Date[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
    days.push(new Date(t));
  }
  return days;
}

export default function LeadsByDayChart({ data }: LeadsByDayChartProps) {
  const { aggregations, goToFilteredLeads, refetch, start, end } = data;

  const countByDate = new Map(aggregations.byDay.map((item) => [item.date, item.count]));

  // The chart must reflect the same start/end range as the rest of the
  // Analytics tab (and its own aggregations.byDay) — it previously always
  // plotted a fixed "last 14 days from now", which silently diverged from
  // whatever range was actually selected in the date filters above it.
  const days = buildDayRange(start, end);

  const series = days.map((day) => {
    const iso = toIsoDate(day);
    return {
      iso,
      label: `${MONTHS[day.getMonth()]} ${day.getDate()}`,
      count: countByDate.get(iso) ?? 0,
    };
  });

  // Skip labels once the range grows past ~15 days so the x-axis doesn't
  // become an unreadable wall of overlapping text.
  const labelInterval = Math.max(0, Math.ceil(series.length / 15) - 1);

  return (
    <Paper variant="outlined" data-tracker="938" sx={{ mt: 2.5, p: "20px 24px", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, color: "#1a1a1a" }}>
          Leads by Day
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refetch} sx={{ color: BRAND_COLOR }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#eeeeee" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9e9e9e" }} interval={labelInterval} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9e9e9e" }} />
          <RechartsTooltip />
          <Line
            type="linear"
            dataKey="count"
            stroke={BRAND_COLOR}
            strokeWidth={2.5}
            dot={(dotProps: any) => (
              <circle
                key={dotProps.payload.iso}
                cx={dotProps.cx}
                cy={dotProps.cy}
                r={6}
                fill="#ffffff"
                stroke={BRAND_COLOR}
                strokeWidth={2}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  goToFilteredLeads({ start: dotProps.payload.iso, end: dotProps.payload.iso })
                }
              />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
